import { DialogContent, DialogTitle, Stack } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { paperGoodsIssueBusiness } from "api/bussiness/paper-material/paper-goods-issue";
import { invalidatePaperCatalogCache } from "api/cached";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import { IControl } from "commons/ultilities/dynamic-form/models/control";
import { createPaperMaterialOptionLabel } from "commons/ultilities/option-item/label-ultil";
import { observer } from "mobx-react-lite";
import { IUpdatePaperGoodsIssue } from "mobx-stores/_models/paper-inventory/paper-goods-issue/paper-goods-issue";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { DialogForm } from "share-components/templates/form/dialog-form";
import {
  GetArrayItemFn,
  useGoodsIssueItemHandlers,
} from "../hooks/use-goods-issue-item-handlers";
import { useRollNumber } from "../hooks/use-roll-number";
import { getUpdatePaperGoodsIssueSchema } from "./schema";

export const UpdatePaperGoodsIssue = observer(() => {
  const queryClient = useQueryClient();
  const {
    paperInventoryManagement: {
      paperGoodsIssueStore: {
        openEdit,
        getPaperGoodsIssue,
        paperGoodsIssueDetail,
        setProp,
      },
    },
  } = useStores();
  const { getRollNumberOptions, getBrandByRollNumber } = useRollNumber();
  const {
    form: { controls, isValid },
    getControl,
    submitHandler,
    setDefaults,
    getArrayItemControl,
  } = useForm<IUpdatePaperGoodsIssue>(getUpdatePaperGoodsIssueSchema(), {
    id: paperGoodsIssueDetail?.id,
    issueDate: paperGoodsIssueDetail?.issueDate,
    items: [],
  });

  const { itemValueChangedHandler, fillOptionItemsForRollNumber } =
    useGoodsIssueItemHandlers({
      getArrayItemControl: getArrayItemControl as unknown as GetArrayItemFn,
      getRollNumberOptions,
      getBrandByRollNumber,
    });

  useEffect(() => {
    if (paperGoodsIssueDetail) {
      setDefaults({
        id: paperGoodsIssueDetail?.id,
        issueDate: paperGoodsIssueDetail?.issueDate,
        items: paperGoodsIssueDetail.items.map((x) => ({
          id: x.id,
          materialId: x.materialId,
          amount: x.amount,
          workOrderId: x.workOrderId,
          workOrderPOId: x.workOrderPOId,
          brand: x.brand,
          rollNumber: x.rollNumber,
          woNumber: x.woNumber,
          length: x.length,
          sheetQuantity: x.sheetQuantity,
          unitsPerSheet: x.unitsPerSheet,
          pcsAmount: x.pcsAmount,
          isTakeAll: x.isTakeAll,
          isWaste: x.isWaste,
        })),
      });

      if (
        paperGoodsIssueDetail.items &&
        paperGoodsIssueDetail.items.length > 0
      ) {
        getControl("items").children.forEach((row: IControl[], idx: number) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = paperGoodsIssueDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value,
          )?.[0];

          const materialIdCtr = row.filter((x) => x.name === "materialId")?.[0];
          if (materialIdCtr && data?.material) {
            materialIdCtr.setProp("optionItems", [
              {
                label: createPaperMaterialOptionLabel(data.material),
                value: data.material?.id,
              },
            ]);
          }

          const woCtr = row.filter((x) => x.name === "workOrderPOId")?.[0];
          if (woCtr && data.workOrderPO) {
            woCtr.setProp("optionItems", [
              {
                value: data?.workOrderPOId,
                label: `${data?.workOrderPO?.woNumber || ""}`,
              },
            ]);
          }

          const rollNumberCtr = row.find((x) => x.name === "rollNumber");
          if (rollNumberCtr) {
            fillOptionItemsForRollNumber(rollNumberCtr, materialIdCtr.value);
          }
        });
      }
    }
  }, [
    controls,
    getControl,
    paperGoodsIssueDetail,
    paperGoodsIssueDetail?.id,
    paperGoodsIssueDetail?.issueDate,
    paperGoodsIssueDetail?.items,
    setDefaults,
    fillOptionItemsForRollNumber,
    getArrayItemControl,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdatePaperGoodsIssue) => {
      try {
        if (!paperGoodsIssueDetail?.id) return;
        await paperGoodsIssueBusiness.update(paperGoodsIssueDetail?.id, data);
        await invalidatePaperCatalogCache(queryClient);
        await getPaperGoodsIssue();
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getPaperGoodsIssue, paperGoodsIssueDetail?.id, queryClient, setProp],
  );

  const itemsControl = useMemo(() => {
    return getControl("items").children;
  }, [getControl]);

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"sm"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa xuất kho</DialogTitle>
      <DialogContent>
        <Stack sx={{ marginTop: "10px" }}>
          <DynamicForm controls={controls}></DynamicForm>
          {itemsControl && itemsControl.length > 0 && (
            <DynamicFormArray
              controlSpacing={2}
              children={itemsControl}
              onValueChanged={itemValueChangedHandler}
            ></DynamicFormArray>
          )}
        </Stack>
      </DialogContent>
    </DialogForm>
  );
});
